import { z } from 'zod';
import { CaptureKindSchema } from './vocabulary.js';
import { FileTopicSchema } from '../files/vocabulary.js';
import { FixSchema } from '../common/geo.js';
// The tickets module's wire — standard platform API (any client: web, mobile,
// the bot on-behalf). The capture ingest's payload is the one provider-shaped
// member (Telegram file ids — the server pulls the bytes; ADR-068 §4 pull by
// reference); DATA-CONTRACTS §Telegram points here. A capture names its SITE;
// the server routes it to the work at hand (DOMAIN-RULES §7) — no client ever
// creates a container.
const Id16 = z.string().min(1).max(16);
/** The GPS reading is the platform's shared shape (common/geo — the nearby
 *  read takes the same one); re-exported so the ceremony's consumers keep
 *  their import path. */
export { FixSchema } from '../common/geo.js';
// ── the visit ceremonies ─────────────────────────────────────────────────────
export const CheckInRequestSchema = z.object({ site_id: Id16, fix: FixSchema.optional() }).strict();
/** The recovery read (a restarted bot re-learns its state from the server —
 *  presence is server truth). v1 serves exactly this question. */
export const VisitsListQuerySchema = z.object({ person: z.literal('me'), status: z.literal('open') }).strict();
// ── the capture ingest (the bot's write path) ───────────────────────────────
/** One of Telegram's ready-made sizes (a photo's PhotoSize; a video's thumbnail).
 *  Producer-made derivatives — no image processing in ems-app (ADR-068 §3). */
const TelegramSizeSchema = z
    .object({
    file_id: z.string().trim().min(1).max(128),
    width: z.number().int().positive().max(10_000),
    height: z.number().int().positive().max(10_000),
    file_size: z.number().int().nonnegative().optional(),
})
    .strict();
/** The provider half, forwarded verbatim from the bot's Telegram update — the
 *  sizes arrive FREE in the message (there is no "ask for sizes" call).
 *  `file_unique_id` is Telegram's stable content id (our idempotency);
 *  `file_id` is the download token (our fetch). */
export const TelegramMediaSchema = z
    .object({
    file_id: z.string().trim().min(1).max(128),
    file_unique_id: z.string().trim().min(1).max(64),
    mime_type: z.string().trim().min(1).max(127).optional(),
    file_name: z.string().trim().min(1).max(255).optional(),
    file_size: z.number().int().nonnegative().optional(),
    width: z.number().int().positive().max(10_000).optional(),
    height: z.number().int().positive().max(10_000).optional(),
    duration_ms: z.number().int().nonnegative().max(86_400_000).optional(),
    /** A photo's size ladder (largest is the original we fetch). */
    sizes: z.array(TelegramSizeSchema).max(10).optional(),
    /** A video's producer-made thumbnail — or an UNCOMPRESSED photo's (sent
     *  "as file": a `document` with an image mime, no size ladder). */
    thumbnail: TelegramSizeSchema.optional(),
})
    .strict();
export const CaptureIngestRequestSchema = z
    .object({
    /** Where it was taken — the server routes it to the work at hand there:
     *  the open ticket the person is on (none exist in v1) · else the walk-up
     *  container of their visit (found or created; a late send joins the last
     *  visit within the project's cap; no visit = an episode container). */
    site_id: Id16,
    /** The person's CHOICE when the server answered several candidates (the
     *  reserved `CAPTURE_TARGET_AMBIGUOUS` — DATA-CONTRACTS), or an explicit
     *  target: must be at `site_id`, of the capture class (v1), theirs. */
    ticket_id: Id16.optional(),
    kind: CaptureKindSchema,
    telegram: TelegramMediaSchema,
    /** The technician's words (Telegram's caption cap). */
    caption: z.string().trim().min(1).max(1024).optional(),
    /** Tapped topics — the files vocabulary validates them exactly as the web answer's. */
    topics: z.array(FileTopicSchema).max(8).optional(),
    /** Tapped assets (the `human` source; bounded to the ticket's site like the web answer).
     *  The `request` source is reserved in prose (DATA-CONTRACTS) until requests exist. */
    subjects: z.array(Id16).max(8).optional(),
    /** The phone's send moment (the message's date) — the honest clock; frozen as `files.captured_at`. */
    captured_at: z.string().datetime({ offset: true }),
})
    .strict();
// ── the hub — the list read (ADR-073 §7 · ADR-078) ───────────────────────────
// The two arms of visibility are the SERVER's (the read key over scope, or
// participation); the query carries only the lenses. `stage` absent = the
// DEFAULT LENS (open work: the three open stages); `closed` = the Closed
// WINDOW on the closing time — its filed rows join whatever open stages are in
// view (absent = hidden: no history read, none counted); `stage=closed` alone
// = the filed rows of the window alone; `fact` = the facts group
// (stored columns, never a scan); `group_by` + `open` = the bands with their
// true counts and the STREAM of the opened bands' rows (ADR-078 §4).
import { csv } from '../common/query.js';
import { LocationParamSchema } from '../common/location.js';
import { PaginationSchema } from '../common/pagination.js';
import { PeriodParamSchema, PeriodZoneSchema, zoneRequiredWith } from '../common/period.js';
import { BAND_ALL, BandKeySchema } from '../common/bands.js';
import { CaptureStageSchema, TicketClassSchema, TicketFactSchema, TICKET_GROUP_BY, TICKET_RECENCY_BANDS, TICKET_SORT_KEYS } from './vocabulary.js';
/** The list's FILTERS — the lenses that are not a stage, a window, a page or an order: what a bulk act over "the rows
 *  in view" sends (`TicketsCloseRequestSchema`), the one shape both schemas are built from. */
const TICKET_FILTERS = {
    /** Typed search (ADR-072): `#128` names that ticket alone · a plain number is a word — the ticket of that number and
     *  the tickets whose site's name or code holds it · the site's name (a typed mistake forgiven as a fallback) and its
     *  references (one typed exactly and whole: that site's tickets alone) · the person's name (contains). ≤ 64 chars. */
    q: z.string().trim().max(64).optional(),
    fact: csv(TicketFactSchema),
    /** The Location filter (ADR-037 §2 — kinded tokens, picks add up): `site:<id>` · `depot:<id>` · `vehicle:<id>` (its
     *  stock hold) · `<kind>:*`. The page offers Sites today (a ticket is born at a site); the grammar reads all three. */
    location: LocationParamSchema,
    /** Who filed it — user ids, or the token `me` for the viewer (a link and a saved view carry it; the server resolves it — ADR-080 §6). */
    person: csv(z.string().min(1).max(16)),
    class: csv(TicketClassSchema),
    /** The Created filter (ADR-082 §5): a period word or a day pair on the ticket's CREATION (a work surface's date
     *  is when the work was born) — the days cut in the screen's zone `tz`, which must come with it. */
    created: PeriodParamSchema.optional(),
    tz: PeriodZoneSchema,
};
export const TicketsListQuerySchema = PaginationSchema.extend({
    ...TICKET_FILTERS,
    stage: csv(CaptureStageSchema),
    /** The Closed chip's WINDOW (ADR-078 §2): the filed rows join the population through a period on the CLOSING time —
     *  a word or a day pair, cut in `tz` like `created`; absent = hidden (nothing filed is read or counted). `stage=closed`
     *  ("Only closed tickets") is admitted only with it. */
    closed: PeriodParamSchema.optional(),
    sort_by: z.enum(TICKET_SORT_KEYS).default('last_activity'),
    /** Absent = the key's useful default (TICKET_SORT_DEFAULT_DIR); a header's second click flips it. */
    sort_dir: z.enum(['asc', 'desc']).optional(),
    /** The grouped view (ADR-078 §4): the answer carries every band of the population with its true count (`bands[]`)
     *  and the rows of the OPENED bands alone, in band order then the sort, paged by `limit`/`offset` like the plain
     *  list, each row stamped with its `band`. Recency is cut in the screen's zone: `tz` must come with it. */
    group_by: z.enum(TICKET_GROUP_BY).optional(),
    /** The opened bands' keys (one of the hub's rungs · a person's id), or `*` for every band; absent = every band
     *  folded — the bands with their counts and no rows (how a grouped view arrives). A stale id is a tolerant read
     *  (the server answers the bands it has); a word off the SURFACE's rungs — any other ladder word included — is
     *  `INVALID_BAND`. Without `group_by` the open set is ignored (the count face shares this schema). */
    open: csv(BandKeySchema),
})
    .strict()
    .superRefine((v, ctx) => {
    zoneRequiredWith(['created', 'closed'])(v, ctx);
    if (v.stage?.includes('closed') && v.closed === undefined)
        ctx.addIssue({ code: 'custom', path: ['stage'], message: 'CLOSED_WINDOW_REQUIRED' });
    // The ladder is cut at midnight in the screen's zone, like a period (ADR-082 §6): recency bands need `tz`.
    if (v.group_by === 'recency' && v.tz === undefined)
        ctx.addIssue({ code: 'custom', path: ['tz'], message: 'TIMEZONE_REQUIRED' });
    if (v.group_by === 'recency' && v.open?.some((k) => k !== BAND_ALL && !TICKET_RECENCY_BANDS.includes(k)))
        ctx.addIssue({ code: 'custom', path: ['open'], message: 'INVALID_BAND' });
});
/** The batch close — "Close completed · n" (ADR-073 §2 · ADR-078 §4): the
 *  server SELECTS the Completed rows by the page's own FILTERS — the list's
 *  filter subset, never the loaded rows' ids — so n is the strip's number and
 *  the act matches it; at most `TICKET_BATCH_CLOSE_MAX` per act, the remainder
 *  named. No stage (Completed is forced), no window (nothing filed is closed
 *  again), no page, no order, no grouping. */
export const TicketsCloseRequestSchema = z.object(TICKET_FILTERS).strict().superRefine(zoneRequiredWith(['created']));
